import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Container,
  Grid,
  makeStyles,
  NavigationAvatar,
  useMediaQuery,
  NavigationCustomers,
  useTheme,
} from '@dxb-web-ui-framework/design-system';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAppAuthenticationDispatch,
  useAppAuthenticationDispatchOutput,
} from '@dxb-web-ui-framework/iam/contexts/AppAuthentication/hooks';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { AccountBalance } from '@dxb-web-ui-framework/design-system/dist/icons';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import {
  useUserInfo,
  useCustomerList,
  useSelectedCustomerId,
} from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { useHasDivider } from '../../../contexts/Header/hooks';
import { getRoutePath } from '../../Routes/routePaths';
import { HeaderLogo } from './HeaderLogo';
import { MainMenu } from './MainMenu';
import { NotificationMenu } from './NotificationMenu';
import { TransactionsNotification } from './TransactionsNotification';

const useStyles = makeStyles(theme => ({
  backdrop: {
    backdropFilter: 'blur(10px)',
  },
  container: {
    display: 'flex',
    height: '100%',
    overflowX: 'hidden',
    position: 'relative',
  },
}));

// TODO: to get the values from proper providers
const alternateText = 'Ecommbx';

export type HeaderProps = {
  hasHeaderElements: boolean;
  brandConfig: GenericObject;
};

const Header: FC<HeaderProps> = ({ hasHeaderElements, brandConfig }): JSX.Element => {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const theme = useTheme();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const mediaUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const classes = useStyles();
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const customerList = useCustomerList();
  const selectedCustomerId = useSelectedCustomerId();
  const { clearAuthDetails }: useAppAuthenticationDispatchOutput = useAppAuthenticationDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  // TODO: pass correct data from provider and delete these dummy data
  const notifications = Array(10);
  const hasDivider = useHasDivider();

  for (let i = 0; i < notifications.length / 2; i++) {
    notifications[i] = {
      id: i + 1,
      title: 'New message',
      subtitle: 'George Brown',
      date: '26/02/2022',
      severity: 'default',
    };
  }

  for (let i = notifications.length / 2; i < notifications.length; i++) {
    notifications[i] = {
      id: i + 1,
      title: 'New message',
      subtitle: 'George Brown',
      date: '26/02/2022',
      severity: 'warning',
    };
  }

  const handleCustomerIdSelectChange = useCallback(
    (customerId: string) => {
      navigate(getRoutePath({ routeKey: 'dashboard', customerId }));
    },
    [navigate],
  );

  const onToggleMainMenu = useCallback(() => {
    setIsMainMenuOpen(prev => !prev);
  }, []);

  const userInitials = useMemo(
    () => `${userInfo?.firstname?.charAt(0)?.toUpperCase() ?? ''}${userInfo?.lastname?.charAt(0)?.toUpperCase() ?? ''}`,
    [userInfo],
  );

  const zIndex = isMobile ? 10 : 'appBar';
  const shouldRenderNotifications =
    location.pathname.includes('dashboard') ||
    location.pathname.includes('gdpr') ||
    location.pathname.includes('error') ||
    location.pathname.includes('success') ||
    location.pathname.includes('fund-transfer');

  return (
    <Box
      component={'header'}
      className={`${classes.backdrop} mui-fixed`}
      position={'fixed'}
      bgcolor='rgba(255, 255, 255, 1)'
      width={'100%'}
      height={theme.spacing(11)}
      lineHeight={0}
      zIndex={zIndex}>
      {/*The Drawer of Header */}
      <MainMenu
        isMainMenuOpen={isMainMenuOpen}
        onToggleMainMenu={onToggleMainMenu}
        userInfo={userInfo}
        userInitials={userInitials}
        onLogout={clearAuthDetails}
      />
      <NotificationMenu
        isNotificationMenuOpen={isNotificationMenuOpen}
        onClose={() => setIsNotificationMenuOpen(false)}
        notifications={notifications}
      />
      <Container maxWidth={'lg'} className={classes.container}>
        <Grid container alignItems={'center'} wrap={isMobile ? 'nowrap' : 'wrap'}>
          <HeaderLogo
            logo={isMobile ? brandConfig.logoMobile : brandConfig.logoDesktop}
            isMobile={isMobile}
            alternateText={alternateText}
            customerId={selectedCustomerId}
          />
          {hasHeaderElements && (
            <>
              {(shouldRenderNotifications || !isMobile) && <TransactionsNotification />}
              {/*TODO: take total of unread messages as prop and display variant (dot to default) if it has unread messages*/}
              <Box ml='auto' pr={mediaUpSm ? 2 : 1} pl={mediaUpSm ? 2 : 1}>
                {/*<Button*/}
                {/*  isIconButton*/}
                {/*  size='large'*/}
                {/*  variant='outlined'*/}
                {/*  color='default'*/}
                {/*  aria-label={*/}
                {/*    notificationsCount*/}
                {/*      ? `${t('App.Header.PendingNotifications')}`*/}
                {/*      : `${t('App.Header.NoPendingNotifications')}`*/}
                {/*  }*/}
                {/*  onClick={() => setIsNotificationMenuOpen(true)}>*/}
                {/*  <Badge*/}
                {/*    variant={notificationsCount ? 'dot' : 'standard'}*/}
                {/*    color={notificationsCount ? 'primary' : 'default'}*/}
                {/*    overlap='circular'>*/}
                {/*    <SvgIcon color='secondary' fontSize='medium'>*/}
                {/*      <Notifications />*/}
                {/*    </SvgIcon>*/}
                {/*  </Badge>*/}
                {/*</Button>*/}
              </Box>

              <Box pr={mediaUpSm ? 2 : 1}>
                {customerList && (
                  <NavigationCustomers
                    icon={<AccountBalance />}
                    buttonLabel={
                      customerList.find(customer => customer.customerId === selectedCustomerId)?.description || '-'
                    }
                    items={customerList.map(customer => ({
                      id: customer.customerId,
                      label: customer.description,
                      onClick: () => handleCustomerIdSelectChange(customer.customerId),
                    }))}
                  />
                )}
              </Box>

              <Box mr={0}>
                <NavigationAvatar
                  altText={t('App.Header.NavigationAvatar.AltText')}
                  onClick={onToggleMainMenu}
                  disabled={false}>
                  {userInitials}
                </NavigationAvatar>
              </Box>
            </>
          )}
        </Grid>
      </Container>
      {hasDivider && <Divider />}
    </Box>
  );
};

export { Header };
